export const getBackendUrl = () => {
  const envBase = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_PARSER_URL || '').trim();
  if (envBase) {
    return envBase.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined') {
    const origin = window.location.origin || '';
    if (origin.includes('localhost:3000') || origin.includes('127.0.0.1:3000')) {
      return 'http://localhost:5000';
    }
    if (origin.includes('localhost:5173')) {
      return 'http://localhost:5000';
    }
  }

  return '';
};

export const withBackend = (path = '') => {
  const base = getBackendUrl();
  if (!path) {
    return base || '';
  }
  if (!base) {
    return path;
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const suffix = path.startsWith('/') ? path : '/' + path;
  return base + suffix;
};

// Normalize an image URL for safe use in <img src>. Returns a clean
// data:/http(s)/blob:/relative URL, or '' for anything that would make
// the browser fire a bogus request.
//
// Background: legacy + partially-failed-offload rows put RAW base64 into
// thumbnail_url / preview_url (no data: prefix). When that hits <img src>
// the browser resolves it as a RELATIVE URL and GETs a multi-hundred-KB
// path → 414 Request-URI Too Large, and a burst of those over one HTTP/2
// connection tears the connection down (ERR_CONNECTION_RESET /
// ERR_HTTP2_PROTOCOL_ERROR / ERR_CONNECTION_CLOSED), taking even valid
// Storage thumbnails down as collateral. The previous guard missed
// base64 that (a) contains whitespace/newlines (MIME 76-col wrapping),
// (b) lacks trailing '=', or (c) is a JPEG payload starting with '/9j/'
// (which slipped through the startsWith('/') allow). This version closes
// all three.
export const normalizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return '';

  // data:image/... — collapse duplicated prefixes, strip whitespace from
  // the payload, and reject empties. A data URL whose payload is empty or
  // whitespace-corrupt won't render.
  if (trimmed.startsWith('data:')) {
    const lastBase64 = trimmed.lastIndexOf('base64,');
    if (lastBase64 === -1) return trimmed;
    const payload = trimmed.slice(lastBase64 + 'base64,'.length).replace(/\s+/g, '');
    if (!payload) return '';
    return `data:image/png;base64,${payload}`;
  }

  // Whitespace-stripped copy for the base64-blob detection below.
  const compact = trimmed.replace(/\s+/g, '');

  // A long string that is ONLY base64 alphabet (incl. '/' and '+', so this
  // also catches JPEG '/9j/...' payloads) is a raw base64 blob, never a
  // real URL/path. 256 is far longer than any genuine relative thumbnail
  // path (/thumbnail/<hash>/<n>) and far shorter than an image payload.
  if (/^[A-Za-z0-9+/=]+$/.test(compact) && compact.length > 256) {
    return '';
  }

  // Legacy offload corruption: a valid Supabase Storage object URL with a base64
  // tail glued onto it, e.g. ".../<hash>.png:image/png;base64,iVBOR...". These
  // pass the https check below, and once a ?width= transform is appended they
  // become multi-hundred-KB URLs → 414 Request-URI Too Large, whose bursts tear
  // down the shared HTTP/2 connection and take valid thumbnails with them. Trim
  // back to the real object path (up to and including the image extension). A
  // clean URL or a normal ?query is left untouched (the trailing alternation
  // only matches the corruption signature: a colon, ';base64,', or a long b64
  // run immediately after the extension — never a '?').
  const storageJunk = trimmed.match(
    /^(https?:\/\/\S*?\/storage\/v1\/(?:object|render\/image)\/\S*?\.(?:png|jpe?g|webp|gif))(?:[:;]|%3[Aa]|[A-Za-z0-9+/]{24,})/i
  );
  if (storageJunk) return storageJunk[1];

  // Genuine URLs and short backend paths.
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('blob:')) return trimmed;
  if (trimmed.startsWith('/')) return trimmed; // e.g. /thumbnail/<hash>/<n>

  // Short orphaned base64 fragment (legacy tail) — discard.
  if (/^[A-Za-z0-9+/]+={0,2}$/.test(compact) && compact.length > 16) {
    return '';
  }
  return trimmed;
};

// Grid-thumbnail variant of a stored image URL. Supabase Storage serves the
// raw ~700KB 1280x720 slide PNG by default; rendering dozens of those in the
// dashboard grid saturates the single HTTP/2 connection and tears it down
// (ERR_HTTP2_PROTOCOL_ERROR / ERR_CONNECTION_CLOSED). Supabase's on-the-fly
// image transform shrinks the SAME object to ~100KB. We rewrite
// /object/public/ → /render/image/public/ and ask for width=400&quality=75.
// Non-Supabase URLs (data:, blob:, /thumbnail/<hash>, other hosts) pass through
// normalizeImageUrl unchanged. Use this ONLY for small grid cards, never for the
// full-size preview/workspace where the full-resolution image is wanted.
export const toThumbnailUrl = (url, width = 400, quality = 75) => {
  const normalized = normalizeImageUrl(url);
  if (!normalized || typeof normalized !== 'string') return normalized || '';
  const marker = '/storage/v1/object/public/';
  if (!normalized.includes(marker)) return normalized;
  const rendered = normalized.replace(marker, '/storage/v1/render/image/public/');
  const sep = rendered.includes('?') ? '&' : '?';
  return `${rendered}${sep}width=${width}&quality=${quality}&resize=contain`;
};
